import { motion } from "framer-motion";
import { AlertCircle, Inbox, Loader2 } from "lucide-react";
import { EASE } from "../landing/shared";

export function PageHeader({ title, sub, children, testId }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: EASE }}
      className="flex flex-wrap items-end justify-between gap-4 mb-8"
      data-testid={testId}
    >
      <div>
        <h1 className="font-display font-medium text-3xl sm:text-4xl tracking-[-0.02em] text-[#0F172A]">{title}</h1>
        {sub && <p className="text-sm text-slate-500 mt-2">{sub}</p>}
      </div>
      {children}
    </motion.div>
  );
}

export function Panel({ children, className = "", testId }) {
  return (
    <section
      data-testid={testId}
      className={`bg-white border border-slate-200/80 rounded-2xl shadow-[0_1px_3px_rgba(15,23,42,0.05)] ${className}`}
    >
      {children}
    </section>
  );
}

export function StatCard({ icon: Icon, label, value, sub, accent, loading, testId }) {
  return (
    <div
      data-testid={testId}
      className="bg-white border border-slate-200/80 rounded-2xl shadow-[0_1px_3px_rgba(15,23,42,0.05)] p-6 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_30px_rgba(37,99,235,0.1)] hover:border-[#2563EB]/25"
    >
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-slate-400">{label}</p>
        <span className="w-9 h-9 rounded-xl border border-slate-100 flex items-center justify-center" style={{ backgroundColor: `${accent}14` }}>
          <Icon className="w-4 h-4" style={{ color: accent }} strokeWidth={1.8} />
        </span>
      </div>
      {loading ? (
        <div className="h-9 w-16 rounded-lg bg-slate-100 animate-pulse mt-3" />
      ) : (
        <p className="font-display font-semibold text-4xl tracking-tight text-[#0F172A] mt-3">{value}</p>
      )}
      {sub && !loading && <p className="text-xs text-slate-400 mt-1.5">{sub}</p>}
    </div>
  );
}

export function EmptyState({ title, sub, testId }) {
  return (
    <div data-testid={testId} className="flex flex-col items-center justify-center py-14 text-center">
      <div className="w-14 h-14 rounded-2xl bg-slate-50 border border-slate-200/80 flex items-center justify-center mb-4">
        <Inbox className="w-6 h-6 text-slate-300" strokeWidth={1.5} />
      </div>
      <p className="font-display text-lg text-slate-700 tracking-tight">{title}</p>
      {sub && <p className="text-sm text-slate-400 mt-1.5 max-w-sm">{sub}</p>}
    </div>
  );
}

export function ErrorBanner({ message, onRetry }) {
  if (!message) return null;
  return (
    <div
      data-testid="api-error-banner"
      className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 mb-6"
    >
      <AlertCircle className="w-4 h-4 text-red-500 shrink-0" strokeWidth={1.8} />
      <p className="text-sm text-red-600 flex-1">{message}</p>
      {onRetry && (
        <button
          type="button"
          data-testid="api-error-retry"
          onClick={onRetry}
          className="text-xs font-semibold text-red-500 hover:text-red-700 transition-colors"
        >
          Retry
        </button>
      )}
    </div>
  );
}

export function LoadingRows({ rows = 4 }) {
  return (
    <div className="space-y-3" data-testid="loading-skeleton">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-16 rounded-xl bg-slate-100 animate-pulse" />
      ))}
    </div>
  );
}

export function StatusPill({ status }) {
  const styles = {
    active: "bg-emerald-50 text-emerald-600 border-emerald-200",
    pending: "bg-amber-50 text-amber-600 border-amber-200",
    reviewed: "bg-cyan-50 text-cyan-700 border-cyan-200",
    scheduled: "bg-blue-50 text-blue-600 border-blue-200",
    completed: "bg-slate-50 text-slate-400 border-slate-200",
    on_hold: "bg-orange-50 text-orange-600 border-orange-200",
  };
  const cls = styles[status] || "bg-slate-50 text-slate-500 border-slate-200";
  return (
    <span className={`inline-flex text-[10px] font-semibold tracking-[0.12em] uppercase rounded-full border px-2.5 py-1 ${cls}`}>
      {(status || "").replace(/_/g, " ")}
    </span>
  );
}

export function PriorityPill({ priority }) {
  const styles = {
    high: "bg-red-50 text-red-600 border-red-200",
    medium: "bg-amber-50 text-amber-600 border-amber-200",
    low: "bg-slate-50 text-slate-500 border-slate-200",
  };
  return (
    <span
      data-testid={`priority-${priority}`}
      className={`inline-flex text-[10px] font-bold tracking-[0.14em] uppercase rounded-full border px-2.5 py-1 ${styles[priority] || styles.low}`}
    >
      {priority} priority
    </span>
  );
}

export function SpinnerButton({ loading, children, className = "", ...props }) {
  return (
    <button
      type="button"
      disabled={loading}
      className={`inline-flex items-center justify-center gap-2 disabled:opacity-60 ${className}`}
      {...props}
    >
      {loading && <Loader2 className="w-4 h-4 animate-spin" />}
      {children}
    </button>
  );
}

export function timeAgo(dateInput) {
  const date = new Date(dateInput);
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return "Just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days ago`;
  return date.toLocaleDateString();
}

